import { useEffect, useRef } from 'react'
// react-router's own hooks rather than the portal's useCustomNavigate
// wrapper: the wrapper drops the second argument, and the redirect below
// needs `{ replace: true }` so the hidden page stays out of the history.
import { useLocation, useNavigate } from 'react-router-dom'

import { isAdminOnlyRoute } from '../../constants/adminOnlyRoutes'
import useAdminStealth, { primeAdminStealth } from '../../hooks/useAdminStealth'
import * as routeNames from '../../routes/routeName'
import { authToken } from '../../storage/authToken'

/**
 * SCREEN PRIVACY — the F9 shortcut and the matching route redirect.
 *
 * Press F9 five times and the menus that exist only because of this person's
 * ROLE disappear; five more and they come back. The state is stored against
 * their account, so it survives logging out (see useAdminStealth).
 *
 * Two roles qualify, and the backend decides which:
 *   ADMIN      — the Team menu, STAT Holidays, Delivery Drivers and the rest
 *                of the admin-only navigation.
 *   PHARMACIST — the pharmacist-only navigation, and the pre-recorded clip
 *                published in place of their camera on delivery calls.
 *
 * ORDINARY TEAM MEMBERS ARE UNAFFECTED, and not merely by an ignored keypress:
 * the listener below is not attached at all unless the backend says this user
 * has a role to conceal, so for everyone else there is no handler, no counter
 * and no behaviour to go wrong.
 *
 * Renders nothing.
 */

// Five presses, as specified.
const REQUIRED_PRESSES = 5

// Presses must arrive in a deliberate run. A stray F9 an hour ago must not
// count towards a run started now, so the counter resets when a gap exceeds
// this. Comfortably slow enough to tap by hand, short enough that accidental
// presses spread over time never accumulate into a toggle.
const MAX_GAP_MS = 1500

// Typing guard, matching the one the existing shortcut handler uses
// (assets/js/addShortCutKeys.js): a keystroke aimed at a field belongs to the
// field.
const TYPING_TAGS = ['input', 'textarea', 'select']

const isTypingTarget = () => {
	const active = typeof document !== 'undefined' ? document.activeElement : null
	if (!active) return false

	const tag = active.tagName ? active.tagName.toLowerCase() : ''
	if (TYPING_TAGS.includes(tag)) return true
	if (active.isContentEditable) return true
	// Quill / Jodit rich-text editors, as elsewhere in the portal.
	if (active.classList && active.classList.contains('ql-editor')) return true

	return false
}

const AdminStealthManager = () => {

	const navigate = useNavigate()
	const location = useLocation()

	// Read on every render so the effect below re-runs the moment the token
	// appears (login) or disappears (logout), the same pattern useMyModules
	// uses. This component stays mounted across both.
	const token = authToken()

	const { hidden, canHide, toggle } = useAdminStealth()

	// The login actions prime this before the portal renders, which is what
	// stops the admin menus flashing on screen. A HARD REFRESH has no login
	// action, so resolve it here as well. The request is cached by token, so
	// the two paths together still make exactly one call per session.
	useEffect(() => { primeAdminStealth() }, [token])

	const pressCountRef = useRef(0)
	const lastPressRef = useRef(0)
	// Read the toggle through a ref so the listener can be attached once for
	// the session instead of being torn down and rebuilt on every state
	// change — which would otherwise throw away a part-finished run of presses.
	const toggleRef = useRef(toggle)

	useEffect(() => { toggleRef.current = toggle }, [toggle])

	useEffect(() => {
		// The whole feature is inert for anyone with no role to conceal. The
		// backend decides that (an admin, or a pharmacist), so an ordinary team
		// member never even has the listener attached.
		if (!canHide) return undefined

		const handleKeyDown = (event) => {
			if (event.key !== 'F9') return

			// Leave the modified combinations alone; browsers and screen
			// readers bind some of them.
			if (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return

			if (isTypingTarget()) return

			const now = Date.now()
			const withinRun = now - lastPressRef.current <= MAX_GAP_MS

			pressCountRef.current = withinRun ? pressCountRef.current + 1 : 1
			lastPressRef.current = now

			if (pressCountRef.current >= REQUIRED_PRESSES) {
				pressCountRef.current = 0
				lastPressRef.current = 0
				toggleRef.current()
			}
		}

		document.addEventListener('keydown', handleKeyDown)
		return () => {
			document.removeEventListener('keydown', handleKeyDown)
			pressCountRef.current = 0
			lastPressRef.current = 0
		}
	}, [canHide])

	// Close the direct-address hole: while hidden, an admin sitting on (or
	// navigating to) an admin-only page is moved to the dashboard, the same
	// destination ModuleGuard uses when a module is missing. `replace` keeps
	// the hidden page out of the history, so Back does not bounce them
	// straight into it again.
	useEffect(() => {
		if (!hidden) return
		if (!isAdminOnlyRoute(location.pathname)) return

		navigate(routeNames.DASHBOARD_ROUTE, { replace: true })
	}, [hidden, location.pathname, navigate])

	return null
}

export default AdminStealthManager
